<script setup lang="ts">
import {ref, computed, reactive, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {useClaimStore, useNotificationStore} from '@/store';
import FlightOverview from "@/components/overview/FlightOverview.vue";
import ClaimDetails from "@/components/overview/ClaimDetails.vue";
import Court from '@/components/overview/courts/Court.vue';
import ExpirationBanner from "@/components/ui/banners/ExpirationBanner.vue";
import CancelledClaimBanner from "@/components/ui/banners/CancelledClaimBanner.vue";
import {getRemainingDelayInDays} from './lib/get-remaining-delay';
import { computePermissionForCurrentUser } from '@/utils/directives/v-permission';
import { CheckIcon, DocumentCheckIcon } from '@heroicons/vue/24/outline';

import TravelAgencySignatureCTA from '@/components/states-cta/TravelAgencySignatureCTA.vue';
import SignatureCTA from '@/components/states-cta/ReceivablesContractsCTA.vue';
import ClaimValidationCTA from '@/components/states-cta/ClaimValidationCTA.vue';
import UploadAssignationToRewiew from '@/components/states-cta/UploadAssignationToReview.vue';
import StartClaimCTA from '@/components/states-cta/StartClaimCTA.vue';
import NumeroRGCTA from '@/components/states-cta/NumeroRG-CTA.vue';
import InitProcedureCTA from '@/components/states-cta/InitProcedureCTA.vue';
import FormalNoticeCTA from '@/components/states-cta/FormalNoticeCTA.vue';
import LegalSubpeonaCTA from "@/components/states-cta/LegalSubpeonaCTA.vue";
import ClaimGroups from '@/components/claim/ClaimGroups.vue';
import ClaimStatePreview from '@/components/claim/ClaimStatePreview.vue';
import ClaimTasks from '@/components/Task/ClaimTasks.vue';
// import Novu from '@/components/notifications/Novu.vue';

const {claim, machineState} = storeToRefs(useClaimStore());

async function refreshClaim() {
  await useClaimStore().retrieveClaim(claim.value.id);
}

const delayExpiredSince = computed(() => {
  const days = -getRemainingDelayInDays(claim.value.stateUpdatedAt, claim.value.claimState.expectedDurationInDays);
  if (days == null || days < 0) showBanner.value = false;
  else showBanner.value = true;
  return days;
});
const showBanner = ref(true);

function sendReviewNotification() {
  if (!computePermissionForCurrentUser(["LAWYER"])) return;
  useNotificationStore().clear(); // Tmp fix
  return useNotificationStore().emitNotification({
    title: "Demande de relecture",
    message: "L'assignation est en attente de votre validation pour envoi aux huissiers",
    cta: () => {
      const claimDocument = useClaimStore().claim.claimDocuments.find(d =>
          d.documentType.type === "LEGAL_SUBPOENA_TO_REVIEW"
      );
      window.open(`https://upclaim.app.box.com/file/${claimDocument?.file.boxFileId}`, "_blank");
    },
  }, -1);
}

watch(claim, (newClaim) => {
  const claimDocument = useClaimStore().claim.claimDocuments.find(d => d.documentType.type === "LEGAL_SUBPOENA_TO_REVIEW");
  if (claimDocument && (newClaim.claimState.type === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_ASSIGNATION_GENERATION"
    || newClaim.claimState.type === "COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_GROUP")
  ) {
    sendReviewNotification();
  }
}, {immediate: true})

</script>

<template>
  <div>
    <!-- <Novu /> -->
    <ClaimStatePreview :claim="claim" />

    <div v-if="claim" class="relative">
      
      <!-- State dependant -->
      <!-- @TODO Centralized "switch" component, that would centralize specific action based on state -->
      <ClaimValidationCTA v-if="claim.claimState?.type === 'CLAIM_VALIDATION.WAIT_FOR_ELIGIBILITY_CHECK' && computePermissionForCurrentUser(['UPCLAIM_ADMIN'])" />
      <SignatureCTA v-if="(claim.claimState?.type === 'CLAIM_VALIDATION.CHOICE_OF_CONTACT_MODE' || claim.claimState?.type === 'CLAIM_VALIDATION.WAIT_FOR_PASSENGER_YOUSIGN_SIGNATURE' || claim.claimState?.type === 'CLAIM_VALIDATION.WAIT_FOR_DOCUMENTS_CREATION') && computePermissionForCurrentUser(['UPCLAIM_ADMIN'])" />
      <TravelAgencySignatureCTA v-if="claim.claimState?.type === 'CLAIM_VALIDATION.WAIT_FOR_RECEIVABLE_TRAVEL_AGENCY' && computePermissionForCurrentUser(['UPCLAIM_ADMIN'])" />
      <StartClaimCTA v-if="claim.claimState?.type === 'UPCLAIM.WAIT_FOR_1ST_EMAIL_SEND' && computePermissionForCurrentUser(['UPCLAIM_ADMIN'])" />
      <FormalNoticeCTA v-if="claim.claimState?.type === 'UPCLAIM.WAIT_SENDS_MED' && computePermissionForCurrentUser(['UPCLAIM_ADMIN'])" :claim="claim" />
      <InitProcedureCTA v-if="claim.claimState?.type.startsWith('COURT') && !claim.procedure && computePermissionForCurrentUser(['UPCLAIM_ADMIN', 'LAWYER'])" :claim="claim" />
      <LegalSubpeonaCTA v-if="claim.claimState?.type === 'COURT.WAIT_FOR_JURIDCTION_CHOICE' && computePermissionForCurrentUser(['UPCLAIM_ADMIN', 'LAWYER'])" :claim="claim" />
      <UploadAssignationToRewiew :claim-id="claim.id" v-if="
        (claim.claimState.type === 'COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_ASSIGNATION_GENERATION'
          || claim.claimState.type === 'COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_GROUP')
        && Boolean(!useClaimStore().claim.claimDocuments.find(d =>
          d.documentType.type === 'LEGAL_SUBPOENA_TO_REVIEW'))
        && computePermissionForCurrentUser(['UPCLAIM_ADMIN', 'LAWYER'])
      "/>
      <Court v-if="claim.procedure" @update-claim="refreshClaim" :claim="claim"/>
      <InitProcedureCTA v-if="!claim.procedure && claim.claimEvents.some(e => e.eventType.type === 'COURT.PROCEDURE_REPL')"  :claim="claim" />
      <NumeroRGCTA v-if="!claim.procedure?.identifier && 
        (claim.claimState.type === 'COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_CALENDAR_HEARING' || claim.procedure?.procedureType === 'ESCP')" />
      <!--------------------->
      
      <ClaimTasks v-permission="['UPCLAIM_ADMIN', 'LAWYER']" :claim-id="claim.id" />
      <ClaimGroups v-permission="['UPCLAIM_ADMIN', 'LAWYER']" :claim-id="claim.id" />

      <FlightOverview
        :airline="claim.legalSpecification.effectiveAirline"
        :claim-type-name="claim.claimType.name"
        :flight="claim.legalSpecification.effectiveFlight"
        :rerouting-proposals="claim.legalSpecification.impactedSegment.reroutingProposals"
        :actual-rerouting="claim.legalSpecification.impactedSegment.actualRerouting"
      />
      <ClaimDetails @update="refreshClaim" :claim="claim" />
      <ExpirationBanner v-permission="['UPCLAIM_ADMIN']" :days="delayExpiredSince" v-show="showBanner"
              @close="showBanner = false"/>
      <CancelledClaimBanner v-if="claim.claimLegalStep.name === 'CANCELLED'" v-show="showBanner"
          @close="showBanner = false"/>
    </div>
  </div>
</template>

<style>

</style>